import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userFavorites, setUserFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Define API base URL depending on environment
  const api = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}/api`
  });

  // Set token for all requests if it exists
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    // Check if user is logged in on mount
    const checkLoggedIn = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await api.get('/auth/me');
          setUser(res.data);
          setUserFavorites(res.data.favorites || []);
        } catch (error) {
          console.error("Token invalid or expired");
          localStorage.removeItem('token');
          setUser(null);
          setUserFavorites([]);
        }
      }
      setLoading(false);
    };

    checkLoggedIn();
  }, []);

  const adminLogin = async (identifier, password) => {
    try {
      const res = await api.post('/auth/admin-login', { identifier, password });
      localStorage.setItem('token', res.data.token);
      // Fetch full user profile after login to get favorites
      const meRes = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${res.data.token}` }
      });
      setUser(meRes.data);
      setUserFavorites(meRes.data.favorites || []);
      return { success: true, role: meRes.data.role };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Admin login failed' 
      };
    }
  };

  const login = async (identifier, password) => {
    try {
      const res = await api.post('/auth/login', { identifier, password });
      localStorage.setItem('token', res.data.token);
      // Fetch full user profile after login to get favorites
      const meRes = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${res.data.token}` }
      });
      setUser(meRes.data);
      setUserFavorites(meRes.data.favorites || []);
      return { success: true, role: meRes.data.role };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const res = await api.post('/auth/register', userData);
      localStorage.setItem('token', res.data.token);
      // Fetch full user profile after register
      const meRes = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${res.data.token}` }
      });
      setUser(meRes.data);
      setUserFavorites(meRes.data.favorites || []);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setUserFavorites([]);
  };

  const toggleFavorite = async (propertyId) => {
    if (!user) return false;
    try {
      const res = await api.post('/users/favorites/toggle', { propertyId });
      setUserFavorites(res.data.favorites);
      // Also sync user object
      setUser(prev => ({ ...prev, favorites: res.data.favorites }));
      return true; // Success
    } catch (error) {
      console.error('Failed to toggle favorite', error);
      return false; // Failed
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const res = await api.put('/users/profile', profileData);
      setUser(prev => ({ ...prev, ...res.data }));
      return { success: true };
    } catch (error) {
      console.error('Failed to update profile', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Profile update failed' 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, userFavorites, loading, login, adminLogin, register, logout, toggleFavorite, updateProfile, api }}>
      {children}
    </AuthContext.Provider>
  );
};

